import type { ExperimentListDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { SplitExperimentProps } from './types';
import { SplitActions } from './actions';
import { useQuery } from '@zg-rentals/react-query5';
import { useSplitContext } from './SplitProvider';

import {
  RentalManagerExperimentWebApiHandler_getTreatment,
  RentalManagerUserExperimentWebApiHandler_getTreatment,
  RentalManagerUserExperimentWebApiHandler_list,
} from '@zg-rentals/ts-rental-manager-api-client';

type QueryOptions = {
  onSuccess?: (response: unknown) => void;
  onError?: (err: unknown) => void;
  enabled?: boolean;
  refetchOnMount?: boolean;
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
};

/**
 * `setClient` for @zg-rentals/ts-rental-manager-api-client is required for this hook to work as a comsumable
 */
export const useList = (queryOptions: QueryOptions = {}) => {
  const { dispatch } = useSplitContext();

  const queryFn = async () => {
    try {
      const data = await RentalManagerUserExperimentWebApiHandler_list();
      queryOptions.onSuccess?.(data);
      dispatch(SplitActions.updateSplitFeatureNames(data.featureNames));

      return data;
    } catch (err) {
      queryOptions.onError?.(err);
      throw err;
    }
  };

  return useQuery<ExperimentListDto, Error>({
    queryKey: ['RentalManagerUserExperimentWebApiHandler_list'],
    queryFn,
    ...queryOptions,
    staleTime: Infinity,
    retry: 0,
  });
};

/**
 * sync to GetTreatmentParams in
 * modules/rental-manager-env-api/src/clients/rentalmanagerapi/RentalManagerUserExperimentWebApiHandler.ts
 */
interface GetTreatmentParams {
  featureName: string;
  listingAlias?: string;
}

interface GetTreatmentGuidParams {
  featureName: string;
}

/**
 * `setClient` for @zg-rentals/ts-rental-manager-api-client is required for this hook to work as a comsumable
 */
export const useGetTreatment = (getParams: GetTreatmentParams, queryOptions: QueryOptions = {}) => {
  const { dispatch } = useSplitContext();

  const queryFn = async () => {
    try {
      const data = await RentalManagerUserExperimentWebApiHandler_getTreatment(
        getParams?.featureName ? getParams : { featureName: '' },
      );
      queryOptions.onSuccess?.(data);
      dispatch(SplitActions.updateSplitExperiments(data));

      return data;
    } catch (err) {
      queryOptions.onError?.(err);
      throw err;
    }
  };

  return useQuery<SplitExperimentProps, Error>({
    queryKey: ['RentalManagerUserExperimentWebApiHandler_getTreatment', getParams],
    queryFn,
    ...queryOptions,
    retry: 0,
    throwOnError: false,
  });
};

export const useGetTreatmentGuid = (getParams: GetTreatmentGuidParams, queryOptions: QueryOptions = {}) => {
  const { dispatch } = useSplitContext();

  const queryFn = async () => {
    try {
      const data = await RentalManagerExperimentWebApiHandler_getTreatment(
        getParams?.featureName ? getParams : { featureName: '' },
      );
      queryOptions.onSuccess?.(data);
      dispatch(SplitActions.updateSplitExperiments(data));

      return data;
    } catch (err) {
      queryOptions.onError?.(err);
      throw err;
    }
  };

  return useQuery<SplitExperimentProps, Error>({
    queryKey: ['RentalManagerExperimentWebApiHandler_getTreatment', getParams],
    queryFn,
    ...queryOptions,
    retry: 0,
    throwOnError: false,
  });
};
